import React, { ReactElement } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getBackgroundTheme, getTextColor, type } from '../../style';
import CloseIcon from '@material-ui/icons/Close';
import { Link } from 'react-router-dom';
import OfferActionButton from '../../atoms/offers/OfferActionButton';
import OffersTutorialModal from '../../components/common/modals/OffersTutorialModal';
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import { updateShowTutorialOffer } from '../../actions/profileActions';
import { useDispatch, useSelector } from 'react-redux';
import { CustomStyledProps } from 'style/style';
import breakpoints from 'theme/material/breakpoints';
import colors from 'theme/colors';
import VerifiedBadgeTooltip from 'components/common/VerifiedBadgeTooltip';
import { auth10tixVerification } from 'config/Master';
import { RootState } from 'store';
import { FONT_FAMILIES } from 'theme/material/constants';
import Camera from 'atoms/icons/Camera';
import { generalIcons } from '../../atoms/icons/materialIcons';
import OfferAmountButton from 'atoms/buttons/OfferAmountButton';
import { isMobile } from 'react-device-detect';

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const ReminderContainer = styled('div')(({ isOpen }) => ({
  display: isOpen ? 'block' : 'none',
  position: 'fixed',
  top: '20%',
  left: '50%',
  marginLeft: -200,
  zIndex: 998,

  [breakpoints.down(479.05)]: {
    width: '100%',
  },
}));

const BackdropContainer = styled('div')({
  background: 'rgba(0, 0, 0, 0.4)',
  top: 0,
  left: 0,
  position: 'fixed',
  width: '100%',
  height: '100%',
  textAlign: 'center',
});

const OfferAlertContainer = styled('div')`
  border-radius: 10px;
  display: flex;
  flex-wrap: wrap;
  width: 450px;
  min-height: 400px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
  cursor: pointer;
  position: relative;
  background-clip: padding-box;
  box-sizing: border-box;
  border: solid 5px #10cbbb;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    margin: -5px;
    border-radius: inherit;
    background: linear-gradient(93deg, #10cbbb 0%, #2b8fd7 100%);
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.3);
  }
  ${breakpoints.down(639.05)} {
    width: 345px;
  }
`;

const OfferAvatar = styled('div')(
  {
    width: '100%',
    height: 340,
    background: '#000000',
    flexShrink: 0,
    borderRadius: '7px 7px 0px 0px',
    position: 'relative',
  },
  ({ backgroundUrl }: CustomStyledProps) =>
    backgroundUrl && {
      backgroundImage: `url(${backgroundUrl})`,
      backgroundPosition: 'top',
      backgroundSize: 'cover',
    }
);

const OfferClose = styled('a')({
  fontWeight: 600,
  position: 'absolute',
  right: 30,
  top: 30,
  filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.8))',
  color: '#fff',
  zIndex: 4,
  border: `2px solid ${colors.gray[600]}`,
  borderRadius: '50%',
  width: 22,
  height: 22,
  backgroundColor: 'white',
  padding: 2,
});

const customCloseIconStyle = {
  fontSize: 20,
  color: colors.gray[600],
  stroke: colors.gray[600],
  verticalAlign: 'middle',
};

const OfferProfileContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  color: 'white',
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  zIndex: 3,
});

const OfferProfileDetails = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  textShadow: '0px 0px 4px rgba(0, 0, 0, 0.8)',
  color: '#fff',
  paddingBottom: 20,
  paddingLeft: 25,
  textAlign: 'left',
  [breakpoints.down(639.05)]: {
    paddingLeft: 15,
    paddingBottom: 20,
  },
});

const OfferUsername = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  fontFamily: FONT_FAMILIES.secondary,
  ...type.title.SM,
  letterSpacing: -0.2,
  lineHeight: '100%',
}));

const OfferProfileDetail = styled('div')(() => ({
  fontSize: 14,
  color: colors.dark.black.secondary,
  lineHeight: '100%',
}));

const OfferDescriptionContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  lineHeight: '10px',
});

const OfferDescription = styled('span')({
  fontSize: 10,
  color: colors.dark.black.secondary,
  letterSpacing: -0.3,
  lineHeight: '100%',
});

const OfferProfileLastSeen = styled('span')(() => ({
  fontSize: 10,
  color: colors.dark.black.secondary,
  letterSpacing: -0.3,
  lineHeight: '100%',
}));

const PhotoCountDetails = styled('div')({
  alignSelf: 'end',
  color: colors.dark.black.secondary,
  fontSize: 11,
  lineHeight: '100%',
  paddingBottom: 20,
  paddingRight: 25,
  [breakpoints.down(639.05)]: {
    paddingRight: 15,
    paddingBottom: 20,
  },
});

const PhotoCount = styled('span')(() => ({
  fontSize: 11,
  color: getBackgroundTheme().cardDetails,
}));

const PublicPhotoCount = styled('div')(() => ({
  borderBottom: '1px solid #E21D4A',
  paddingBottom: 3,
  marginBottom: 1,
}));

const Icon = styled('span')({
  paddingRight: '12px',
});

const StyledPhotoCount = styled('span')({
  alignSelf: 'flex-end',
});

const Gradient = styled('div')({
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
  position: 'absolute',
  width: '100%',
  height: '100px',
  bottom: 0,
  left: 0,
  zIndex: 2,
});

const OfferMessageContainer = styled('div')(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    flex: 1,
    padding: '20px 43px',
    borderRadius: '0px 0px 7px 7px',
    backgroundColor: getBackgroundTheme().block,
    [breakpoints.down(639.05)]: {
      padding: '20px 25px',
    },
  })
);

const OfferMessage = styled('div')({
  textAlign: 'center',
});

const OfferLabel = styled('div')(() => ({
  fontSize: 22,
  fontWeight: 600,
  lineHeight: '24px',
  letterSpacing: -0.3,
  ...getTextColor().DARK_4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  [breakpoints.down(639.05)]: {
    fontSize: 18,
  },
}));

const OfferActionsButtonWrap = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 20,
}));

const PriceContainer = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CustomAmountButtonStyle = {
  margin: 0,
  [breakpoints.down(639.05)]: {
    width: 80,
    padding: 8,
    gap: 7,
    '.offer-amount-label': {
      fontSize: 32,
      lineHeight: '21px',
    },
  },
};

const getAcctProfile = account => account && account.profile && account.profile.data;

interface OfferModalProps {
  offer: any;
  isOpen: any;
  avatar: any;
  onClose: any;
  setClickProfileAction: any;
  username: any;
  age: any;
  location: any;
  offerDescription: any;
  lastUpdated: any;
  currency: any;
  price: any;
  offerType: any;
  handleIgnoreOffer: any;
  handleCounter: any;
  counterDisabled: any;
  accountHashId: any;
  handleAcceptOffer: any;
  handleSuggestOffer: any;
  isGenerous: any;
  handleCancelOffer: any;
  handleCancelSuggestion: any;
  photoCount: number;
  privatePhotoCount: number;
}

const OfferModal = ({
  offer,
  isOpen,
  avatar,
  onClose,
  setClickProfileAction,
  username,
  age,
  location,
  offerDescription,
  lastUpdated,
  currency,
  price,
  offerType,
  handleIgnoreOffer,
  handleCounter,
  counterDisabled,
  accountHashId,
  handleAcceptOffer,
  handleSuggestOffer,
  isGenerous,
  handleCancelOffer,
  handleCancelSuggestion,
  photoCount,
  privatePhotoCount,
}: OfferModalProps): ReactElement => {
  useDisableBodyScroll(isOpen);

  const dispatch = useDispatch();

  const account = useSelector((state: RootState) => state.extprofile);
  const { profile: nestedProfile } = useSelector((state: RootState) => state.profile);

  const acctProfile = getAcctProfile(account);

  const isVerified = Boolean(acctProfile && acctProfile.id_verified);
  const isSelfieVerified: boolean = Boolean(nestedProfile && nestedProfile?.data?.selfie_verified);

  const updateOfferTutorialState = () => {
    dispatch(updateShowTutorialOffer(false));
  };

  const validateOfferMessage = (setCurrency, setPrice, offerLabel) => {
    if (!setCurrency || !setPrice) {
      return (
        <React.Fragment>
          No amount suggested. <OffersTutorialModal onClose={updateOfferTutorialState} iconOnly />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <OfferLabel>
          {offerLabel}{' '}
          <OffersTutorialModal
            onClose={updateOfferTutorialState}
            iconOnly
            svgIconHeight={isMobile ? 14 : 15}
            svgIconWeight={isMobile ? 14 : 15}
            svgIconFill={colors.blue[500]}
            linkStyle={{
              fontSize: 'unset',
            }}
          />
        </OfferLabel>
      </React.Fragment>
    );
  };

  const formatPrice = (currency, price) => {
    if (!currency || !price) {
      return '';
    }

    return `${currency.initial}${String.fromCharCode(currency.label)}${price}`;
  };

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <ReminderContainer isOpen={isOpen}>
      {isOpen && (
        <BackdropContainer>
          <OfferAlertContainer data-test-id="OfferHilightModal">
            <OfferAvatar data-test-id="profile-image-offer" backgroundUrl={avatar}>
              <OfferClose
                data-test-id="complete-profile-skip"
                onClick={e => {
                  e.preventDefault();
                  onClose();
                }}
              >
                <CloseIcon style={customCloseIconStyle} />
              </OfferClose>
              <Link
                data-test-id="link-to-profile"
                className="css-offer-profile-list-item-link-desktop"
                onClick={() => {
                  setClickProfileAction(true);
                }}
                to="#"
              >
                <OfferProfileContainer>
                  <OfferProfileDetails>
                    <OfferUsername data-test-id="offer-highlight-username">
                      {username}
                      {((auth10tixVerification && isVerified) || isSelfieVerified) && (
                        <VerifiedBadgeTooltip
                          showToolTip={true}
                          isSelfieVerified={isSelfieVerified}
                          isIdVerified={isVerified}
                          customVerifiedBadgeStyle={{ top: 0 }}
                        />
                      )}
                    </OfferUsername>
                    <OfferProfileDetail data-test-id="offer-highlight-info">
                      {age} • {location}
                    </OfferProfileDetail>
                    <OfferDescriptionContainer>
                      {offerDescription && <OfferDescription>{offerDescription} &bull;</OfferDescription>}
                      <OfferProfileLastSeen>{lastUpdated}</OfferProfileLastSeen>
                    </OfferDescriptionContainer>
                  </OfferProfileDetails>
                  <PhotoCountDetails data-test-id="data-member-photo-count">
                    <PhotoCount>
                      <PublicPhotoCount>
                        <Icon data-test-id="data-camera-icon">
                          <Camera />
                        </Icon>
                        <StyledPhotoCount>{photoCount}</StyledPhotoCount>
                      </PublicPhotoCount>
                      <Icon data-test-id="data-eye-hidden-icon">
                        <generalIcons.eye.hidden />
                      </Icon>
                      <StyledPhotoCount>{privatePhotoCount}</StyledPhotoCount>
                    </PhotoCount>
                  </PhotoCountDetails>
                </OfferProfileContainer>
                <Gradient />
              </Link>
            </OfferAvatar>
            <OfferMessageContainer>
              <OfferMessage>
                <div data-test-id="offer-highlight-title" className="css-offer-profile-price">
                  {validateOfferMessage(
                    currency,
                    price,
                    isGenerous ? 'You got a date offer request!' : 'You got a date offer!'
                  )}
                </div>
              </OfferMessage>
              <OfferActionsButtonWrap>
                {!currency || !price ? null : (
                  <PriceContainer>
                    <OfferAmountButton
                      type={'receive'}
                      price={formatPrice(
                        {
                          initial: currency?.initial ?? '',
                          label: currency?.label ?? '',
                        },
                        price
                      )}
                      viewType={'grid'}
                      hasHoverEffect={false}
                      onClick={() => ''}
                      customBoxStyle={CustomAmountButtonStyle}
                    />
                  </PriceContainer>
                )}
                <OfferActionButton
                  offer={offer}
                  offerType={offerType}
                  isOfferHighlight
                  handleIgnoreOffer={handleIgnoreOffer}
                  handleCounter={handleCounter}
                  counterOfferDisabled={counterDisabled}
                  accountHashId={accountHashId}
                  handleAcceptOffer={handleAcceptOffer}
                  handleSuggestOffer={handleSuggestOffer}
                  currentUserIsGenerous={isGenerous}
                  handleCancelOffer={handleCancelOffer}
                  handleCancelSuggestion={handleCancelSuggestion}
                />
              </OfferActionsButtonWrap>
            </OfferMessageContainer>
          </OfferAlertContainer>
        </BackdropContainer>
      )}
    </ReminderContainer>
  );
};

export default React.memo(OfferModal);
